<main class="review-orders" [class.review-orders--readonly]="isReadOnly()" aria-label="Review Purchase Order Data">
	<app-jjsv-loader
		[isLoading]="isSendToSNLoading() || isRejectSaving() || isConsignmentSaving() || isPlaceOrderSaving() || isDeleteLoading()"
		[message]="isDeleteLoading() ? 'Deleting line item...' : isRejectSaving() ? 'Rejecting document...' : isPlaceOrderSaving() ? 'Placing order...' : 'Loading consignment review...'"
	></app-jjsv-loader>
	<section class="review-orders__container" aria-labelledby="review-orders-title">
		<div class="review-orders__header-row">
			<div class="review-orders__header-copy">
				<div class="review-orders__header-title-wrap">
					<h1 id="review-orders-title" class="review-orders__title">{{ pageTitle() }}</h1> 
					@if(cartStatus() === 'Completed' || cartStatus() === 'Rejected'){
						<span [ngClass]="'review-orders__cart-status-' + cartStatus()">{{cartStatus()}}</span>
					}
				</div>
				@if(cartStatus() !== 'Completed' && cartStatus() !== 'Rejected'){
					<p class="review-orders__subtitle">{{ pageSubtitle() }}</p>
					<p class="review-orders__required-note">Required fields are marked with an asterisk <span>*</span></p>
				}
			</div>

			<button type="button" class="review-orders__ghost-btn" (click)="refresh()" [disabled]="!hasSerialNumber() || isReadOnly()" aria-label="Refresh data">
				Refresh
				<span class="review-orders__icon review-orders__icon--refresh" aria-hidden="true"></span>
			</button>
		</div>

		@if (showInlineWarning()) {
			<section class="review-orders__inline-alert" [class.review-orders__inline-alert--success]="alertType() === 'success'" role="alert" aria-live="polite">
				<div class="review-orders__alert-icon" aria-hidden="true"></div>
				<div>
					<h2 class="review-orders__alert-title">{{ alertTitle() }}</h2>
					<p class="review-orders__alert-text">{{ alertMessage() }}</p>
				</div>
			</section>
		}

		@if (showRejectionDetails()) {
			<section class="review-orders__rejection-details" aria-label="Rejection details">
				@if (displayedRejectionReason()) {
					<div class="review-orders__field">
						<span>Reason for rejection</span>
						<p>{{ displayedRejectionReason() }}</p>
					</div>
				}
				@if (displayedRejectionComment()) {
					<div class="review-orders__field">
						<span>Additional details</span>
						<p>{{ displayedRejectionComment() }}</p>
					</div>
				}
			</section>
		}

		<section class="review-orders__panel" aria-label="Account details">
			<div
				class="review-orders__panel-header"
				role="button"
				tabindex="0"
				(click)="toggleAccount()"
				(keydown.enter)="toggleAccount()"
				(keydown.space)="toggleAccount()"
				[attr.aria-expanded]="accountExpanded()"
			>
				<div class="review-orders__panel-title-wrap">
					<h2 class="review-orders__panel-title">Account</h2>
					@if(!isReadOnly()){
						<button
						type="button"
						class="review-orders__link-btn"
						(click)="$event.stopPropagation(); openAccountModal('SHIP_TO')"
						aria-label="Edit account"
						>Edit</button>
					}
					
				</div>
				<span class="review-orders__panel-chevron" [class.review-orders__panel-chevron--down]="!accountExpanded()" aria-hidden="true"></span>
			</div>

			@if (accountExpanded()) {
				<div class="review-orders__account-grid">
					@for (card of accountCards(); track card.title) {
						<article class="review-orders__account-card">
							<h3>{{ card?.title }}</h3>
							<p>{{ card?.account }}</p>
							<p class="review-orders__account-address">{{ card?.address?.toLocaleUpperCase() }}</p>
						</article>
					}
				</div>
			}
		</section>

		<section class="review-orders__panel" aria-label="Order details">
			<div
				class="review-orders__panel-header"
				role="button"
				tabindex="0"
				(click)="toggleOrderDetails()"
				(keydown.enter)="toggleOrderDetails()"
				(keydown.space)="toggleOrderDetails()"
				[attr.aria-expanded]="orderDetailsExpanded()"
			>
				<h2 class="review-orders__panel-title">Order Details</h2>
				<span class="review-orders__panel-chevron" [class.review-orders__panel-chevron--down]="!orderDetailsExpanded()" aria-hidden="true"></span>
			</div>

			@if (orderDetailsExpanded()) {
				<div class="review-orders__form-grid" [formGroup]="form">
					<label class="review-orders__field">
						<span>Order Type <em>*</em></span>
						<select
							formControlName="orderType"
							[class.review-orders__field-control--invalid]="isFieldInvalid('orderType')"
							(change)="updateHeaderOrderType($event)"
						>
							@for (option of headerOrderTypeOptions(); track option.value) {
								<option [value]="option.value" [disabled]="option.disabled">{{ option.value }}</option>
							}
						</select>
						@if(form.controls.orderType.invalid &&
							(form.controls.orderType.touched || form.controls.orderType.dirty)){
							<div class="review-orders__field-error">
								@if(form.controls.orderType.errors?.['required']){
								<span *ngIf="form.controls.orderType.errors?.['required']">
									Order Type is required.
								</span>
								}
							</div>
						}
					</label>

					<label class="review-orders__field">
						<span>Purchase Order <em>*</em></span>
						<input
							type="text"
							maxlength="35"
							formControlName="purchaseOrder"
							[class.review-orders__field-control--invalid]="isFieldInvalid('purchaseOrder') || headerLengthExceeded().purchaseOrder"
							(beforeinput)="onHeaderFieldBeforeInput('purchaseOrder', $event)"
							(input)="updateHeaderField('purchaseOrder', $event)"
						/>
						@if((form.controls.purchaseOrder.invalid || headerLengthExceeded().purchaseOrder) &&
							(form.controls.purchaseOrder.touched || form.controls.purchaseOrder.dirty || headerLengthExceeded().purchaseOrder)){
							<div class="review-orders__field-error">
								@if(form.controls.purchaseOrder.errors?.['required']){
								<span>
									Purchase Order is required.
								</span>
								}
								@if(form.controls.purchaseOrder.errors?.['maxlength'] || headerLengthExceeded().purchaseOrder){
								<span>Maximum character limit exceeded. Please review the content.</span>
								}
							</div>
						}
					</label>

					@if (showUsFields()) {
						<label class="review-orders__field">
							<span>Shipping Method</span>
							<select
								formControlName="shippingMethod"
								[class.review-orders__field-control--invalid]="isFieldInvalid('shippingMethod')"
								(change)="updateHeaderField('shippingMethod', $event)"
							>
								@for (option of shippingMethodOptions(); track option) {
									<option [value]="option">{{ option }}</option>
								}
							</select>
						</label>
					}

					@if (showUsFields()) {
						<label class="review-orders__field">
							<span>INCOTERMS <small>(Optional)</small></span>
							<select formControlName="incoterms" (change)="updateHeaderField('incoterms', $event)">
								@for (option of incotermOptions(); track option) {
									<option [value]="option">{{ option }}</option>
								}
							</select>
						</label>
					}
					@if (showFranceFields()) {
						<label class="review-orders__field">
							<span>Text for DN <small>(Optional)</small></span>
							<input type="text" maxlength="70" formControlName="deliveryNoteText" (beforeinput)="onHeaderFieldBeforeInput('deliveryNoteText', $event)" (input)="updateHeaderField('deliveryNoteText', $event)" />
							@if((form.controls.deliveryNoteText.errors?.['maxlength'] || headerLengthExceeded().deliveryNoteText) && (form.controls.deliveryNoteText.touched || form.controls.deliveryNoteText.dirty || headerLengthExceeded().deliveryNoteText)){
								<div class="review-orders__field-error"><span>Maximum character limit exceeded. Please review the content.</span></div>
							}
						</label>
					}
					<label class="review-orders__field review-orders__field--span2" [class.review-orders__field--textarea-expanded]="textareaExpanded()">
						<span>Internal Sales Text <small>(Optional)</small></span>
						<div class="review-orders__textarea-wrap" [class.review-orders__textarea-wrap--expanded]="textareaExpanded()">
							<button
								type="button"
								class="review-orders__textarea-toggle"
								(click)="toggleTextarea()"
								[attr.aria-label]="textareaExpanded() ? 'Collapse text area' : 'Expand text area'"
							>
								<img src="/assets/svgs/TextArea_Icon.svg" aria-hidden="true" class="review-orders__textarea-icon" />
							</button>
							<textarea formControlName="salesText" (beforeinput)="onHeaderFieldBeforeInput('salesText', $event)" (paste)="onHeaderFieldPaste('salesText', $event)" (input)="updateHeaderField('salesText', $event)"></textarea>
						</div>
						@if((form.controls.salesText.errors?.['maxlength'] || headerLengthExceeded().salesText) && (form.controls.salesText.touched || form.controls.salesText.dirty || headerLengthExceeded().salesText)){
							<div class="review-orders__field-error"><span>Maximum character limit exceeded. Please review the content.</span></div>
						}
					</label>

					<label class="review-orders__field review-orders__field--full" [ngClass]="{'show-FR-comment': showFranceFields()}">
						<span>Comment (optional)</span>
						<input type="text" maxlength="70" formControlName="comment" (beforeinput)="onHeaderFieldBeforeInput('comment', $event)" (input)="updateHeaderField('comment', $event)" />
						<small>Add an additional context for internal reference. This comment is for internal use only and won’t appear on the order.</small>
						@if((form.controls.comment.errors?.['maxlength'] || headerLengthExceeded().comment) && (form.controls.comment.touched || form.controls.comment.dirty || headerLengthExceeded().comment)){
							<div class="review-orders__field-error"><span>Maximum character limit exceeded. Please review the content.</span></div>
						}
					</label>
				</div>
			}
		</section>
<!-- End of order details panel $$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$  -->
		<section class="review-orders__workspace" aria-label="Document and extracted order rows">
			
			

			<div class="review-orders__workspace-body">
				<aside class="review-orders__pdf-preview" aria-label="Uploaded PDF preview">
					<div class="review-orders__pdf-toolbar" aria-label="PDF controls">
						<div class="review-orders__pdf-docctrl">
							<select class="review-orders__pdf-select" aria-label="Document" [disabled]="!hasPdfDocuments()" (change)="changePdfDocument($event)">
							@for (doc of pdfDocumentOptions(); track doc.documentId) {
								<option [value]="doc.documentId">{{ doc.label }}</option>
							}
							@if (!hasPdfDocuments()) {
								<option value="">No documents</option>
							}
							</select>

							<div class="review-orders__pdf-page">
								<input
									type="number"
									class="review-orders__pdf-page-input"
									[value]="pdfPage()"
									min="1"
								[max]="pdfTotalPages()"
								(change)="setPdfPage(+$any($event.target).value)"
								aria-label="Current page"
							/>
							<span>/ {{ pdfTotalPages() }}</span>
							</div>

							<div class="review-orders__pdf-nav">
								<button
									type="button"
									class="review-orders__pdf-nav-btn"
									(click)="previousPdfPage()"
									[disabled]="!hasPdfDocuments() || pdfPage() === 1"
									aria-label="Previous page"
								>
									<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
										<path fill-rule="evenodd" clip-rule="evenodd" d="M19 12C19 12.5523 18.5317 13 17.9539 13H9.04605C8.46833 13 8 12.5523 8 12C8 11.4477 8.46833 11 9.04605 11H17.9539C18.5317 11 19 11.4477 19 12Z" fill="#888B8D"/>
										<path fill-rule="evenodd" clip-rule="evenodd" d="M12.169 17.7433C11.7584 18.1127 11.1262 18.0795 10.7567 17.669L6.25671 12.669C5.91444 12.2887 5.91443 11.7114 6.2567 11.3311L10.7566 6.33105C11.126 5.92054 11.7583 5.88725 12.1688 6.2567C12.5793 6.62616 12.6126 7.25844 12.2432 7.66896L8.34535 12L12.2433 16.331C12.6127 16.7415 12.5795 17.3738 12.169 17.7433Z" fill="#888B8D"/>
									</svg>
								</button>
								<button
									type="button"
									class="review-orders__pdf-nav-btn"
								(click)="nextPdfPage()"
								[disabled]="!hasPdfDocuments() || pdfPage() === pdfTotalPages()"
									aria-label="Next page"
									
								>
									<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
									<path fill-rule="evenodd" clip-rule="evenodd" d="M6 12C6 11.4477 6.46833 11 7.04605 11H15.9539C16.5317 11 17 11.4477 17 12C17 12.5523 16.5317 13 15.9539 13H7.04605C6.46833 13 6 12.5523 6 12Z" fill="#1E22AA"/>
									<path fill-rule="evenodd" clip-rule="evenodd" d="M12.831 6.25672C13.2415 5.88726 13.8738 5.92053 14.2433 6.33104L18.7433 11.331C19.0855 11.7113 19.0856 12.2886 18.7433 12.6689L14.2434 17.6689C13.874 18.0795 13.2417 18.1127 12.8312 17.7433C12.4206 17.3738 12.3874 16.7416 12.7568 16.331L16.6546 12L12.7567 7.66897C12.3872 7.25846 12.4205 6.62617 12.831 6.25672Z" fill="#1E22AA"/>
									</svg>
								</button>
							</div>
						</div>

						<div class="review-orders__pdf-utils">
							<button type="button" class="review-orders__pdf-util-btn" (click)="rotatePdf()" aria-label="Rotate PDF" [disabled]="!hasPdfDocuments()">
								<img src="assets/svgs/pdf-reload.svg" width="14" height="16" aria-hidden="true" />
							</button>
							<button type="button" class="review-orders__pdf-util-btn" (click)="changeZoom(-10)" aria-label="Zoom out"  [disabled]="!hasPdfDocuments()">
								<img src="assets/svgs/pdf-zoom-out.svg" width="18" height="18" aria-hidden="true" />
							</button>
							<button type="button" class="review-orders__pdf-util-btn" (click)="changeZoom(10)" aria-label="Zoom in"  [disabled]="!hasPdfDocuments()">
								<img src="assets/svgs/pdf-zoom-in.svg" width="18" height="18" aria-hidden="true" />
							</button>
							<button type="button" class="review-orders__pdf-util-btn" (click)="openPdfInNewTab()" aria-label="Open in new tab"  [disabled]="!hasPdfDocuments()">
								<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1E22AA" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
							</button>
						</div>
					</div>

					<div class="review-orders__pdf-placeholder">
						@if (isPdfLoading()) {
							<p class="review-orders__pdf-message" role="status" aria-live="polite">Loading documents...</p>
						} @else if (pdfError()) {
							<p class="review-orders__pdf-message review-orders__pdf-message--error" role="alert">{{ pdfError() }}</p>
						} @else if (!hasPdfDocuments()) {
							<p class="review-orders__pdf-message">No PDF documents are available for this order.</p>
						} @else if (hasPdfSource()) {
							<ngx-extended-pdf-viewer
								[src]="$any(pdfSrc())"
								[page]="pdfPage()"
								(pageChange)="pdfPage.set($event)"
								[zoom]="pdfZoom() + '%'"
								[rotation]="$any(pdfRotation())"
								[showToolbar]="false"
								[showBorders]="false"
								(pagesLoaded)="onPdfPagesLoaded($event)"
								height="100%"
							></ngx-extended-pdf-viewer>
						}
					</div>
				</aside>
				<div class="review-orders__table-wrap" role="region" aria-label="Review order rows" tabindex="0">
					<div class="review-orders__workspace-header">
					<div class="review-orders__table-toolbar" aria-label="Result pagination controls">
						<div class="review-orders__show-wrap">
							<label class="review-orders__show-label" for="review-page-size">Show</label>
							<select
								id="review-page-size"
								class="review-orders__show-select"
								[value]="pageSize()"
								(change)="updatePageSize($event)"
							>
								@for (size of pageSizeOptions; track size) {
									<option [value]="size">{{ size }} items</option>
								}
							</select>
						</div>

						<div class="review-orders__pagination">
							<button type="button" class="review-orders__page-link review-orders__page-link--previous" (click)="previousPage()" [disabled]="currentPage() === 1">
								<span class="review-orders__page-chevron review-orders__page-chevron--previous" aria-hidden="true"></span>
								Previous
							</button>

							@for (token of pageTokens(); track $index) {
								@if (token === '...') {
									<span class="review-orders__ellipsis">...</span>
								} @else {
									<button
										type="button"
										class="review-orders__page-number"
										[class.review-orders__page-number--active]="token === currentPage()"
										(click)="goToPage(token)"
									>{{ token }}</button>
								}
							}

							<button type="button" class="review-orders__page-link review-orders__page-link--next" (click)="nextPage()" [disabled]="currentPage() === totalPages()">
								Next
								<span class="review-orders__page-chevron review-orders__page-chevron--next" aria-hidden="true"></span>
							</button>
						</div>

						<p class="review-orders__results">{{ fromResult() }} - {{ toResult() }} of {{ totalResults() }} Results</p>
					</div>
				</div>
					<table class="review-orders__table">
						<thead>
							<tr>
								<th>Material #</th>
								<th>Serial Number #</th>
								@if (showFranceFields()) {
									<th>Invoice Text</th>
									<th>Delivery Note Text</th>
								}
								<th>Qty</th>
								<th>Order Type</th>
								<th>Ship-to #</th>
								<th>Serial Status</th>
								<th>User Status</th>
								<th></th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							@for (view of pagedRowViews(); track view.row.id) {
								@let row = view.row;
								@let isConsStatus = row.serialStatus === 'ECUS ESTO' && row.userStatus === 'CONS';
								<tr (focusout)="onRowFocusOut($event)">
										<td>
											<input
												type="text"
												[value]="row.material"
												[class.review-orders__field-invalid]="isLineItemFieldInvalid(row.id, 'material')"
												[attr.data-row-field]="row.id + ':material'"
												(input)="updateRowText(row.id, 'material', $event)"
												(keydown.enter)="onRowFieldEnter(row.id, 'material', $event)"
												[disabled]="isReadOnly()"
												aria-label="Material number"
											/>
											@if (lineItemFieldError(row.id, 'material'); as error) {
												<div class="review-orders__field-error">{{ error }}</div>
											}
										</td>
										<td>
											<input
												type="text"
												[value]="row.serialNumber"
												[class.review-orders__field-invalid]="(row.isWarning && row.serialNumber?.trim()) || isLineItemFieldInvalid(row.id, 'serialNumber')"
												[attr.data-row-field]="row.id + ':serialNumber'"
												(input)="updateRowText(row.id, 'serialNumber', $event)"
												(keydown.enter)="onRowFieldEnter(row.id, 'serialNumber', $event)"
												[disabled]="isReadOnly()"
												aria-label="Serial number"
											/>
											@if (lineItemFieldError(row.id, 'serialNumber'); as error) {
												<div class="review-orders__field-error">{{ error }}</div>
											} @else if (row.isWarning && row.serialNumber?.trim()) {
												<div class="review-orders__field-error">Please enter a valid serial number</div>
											}
										</td>
									@if (showFranceFields()) {
											<td>
												<input
													type="text"
													maxlength="70"
													[value]="row.invoiceText"
													[class.review-orders__field-invalid]="lineItemLengthExceeded()[lineItemLengthKey(row.id, 'invoiceText')]"
													[attr.data-row-field]="row.id + ':invoiceText'"
													(beforeinput)="onLineItemTextBeforeInput(row.id, 'invoiceText', $event)"
													(input)="updateRowText(row.id, 'invoiceText', $event)"
													(keydown.enter)="onRowFieldEnter(row.id, 'invoiceText', $event)"
													[disabled]="isReadOnly()"
													aria-label="Invoice text"
												/>
												@if (lineItemLengthExceeded()[lineItemLengthKey(row.id, 'invoiceText')]) {
													<div class="review-orders__field-error">Maximum character limit exceeded. Please review the content.</div>
												}
											</td>
										<td>
											<input
												type="text"
												maxlength="70"
												[value]="row.deliveryNoteText"
												[class.review-orders__field-invalid]="lineItemLengthExceeded()[lineItemLengthKey(row.id, 'deliveryNoteText')]"
												[attr.data-row-field]="row.id + ':deliveryNoteText'"
												(beforeinput)="onLineItemTextBeforeInput(row.id, 'deliveryNoteText', $event)"
												(input)="updateRowText(row.id, 'deliveryNoteText', $event)"
												(keydown.enter)="onRowFieldEnter(row.id, 'deliveryNoteText', $event)"
												[disabled]="isReadOnly()"
												aria-label="Delivery note text"
											/>
											@if (lineItemLengthExceeded()[lineItemLengthKey(row.id, 'deliveryNoteText')]) {
												<div class="review-orders__field-error">Maximum character limit exceeded. Please review the content.</div>
											}
										</td>
									}
									<td>
										<input
											type="number"
											min="1"
											[value]="row.quantity"
											[class.review-orders__field-invalid]="isLineItemFieldInvalid(row.id, 'quantity')"
											[attr.data-row-field]="row.id + ':quantity'"
											(input)="updateRowQuantity(row.id, $event)"
											(keydown.enter)="onRowFieldEnter(row.id, 'quantity', $event)"
											aria-label="Quantity"
												[disabled]="view.quantityDisabled || isReadOnly()"
										/>
												@if (lineItemFieldError(row.id, 'quantity'); as error) {
													<p class="review-orders__field-error">{{ error }}</p>
												}
									</td>
									<td>
										<select
											[class.review-orders__field-invalid]="isLineItemFieldInvalid(row.id, 'orderType')"
											[attr.data-row-field]="row.id + ':orderType'"
											(change)="updateRowText(row.id, 'orderType', $event)"
											(keydown.enter)="onRowFieldEnter(row.id, 'orderType', $event)" [disabled]="isReadOnly()"										>
										@if (!row.orderType) {
												<option value="" disabled [selected]="true">Select Order Type</option>
											}
										@for (option of view.orderTypeOptions; track option.value) {
												@if(!option.disabled){
													<option [value]="option.value" [selected]="option.value === row.orderType" [disabled]="option.disabled">{{ option.value }}</option>
												}
											}
										</select>
											@if (lineItemFieldError(row.id, 'orderType'); as error) {
												<p class="review-orders__field-error">{{ error }}</p>
											}
									</td>
									<td [class.review-orders__ship-to--mismatch]="row.isShipToConflict || row.shipTo !== headerShipTo()" [class.review-orders__ship-to--invalid]="!!row.shipToError">{{ row.shipTo }}
										@if (row.shipToError) {
											<div class="review-orders__field-error">{{ row.shipToError }}</div>
										}
									</td>
									<td [class.review-orders__status--warn]="!isConsStatus">{{ row.serialStatus }}</td>
									<td [class.review-orders__status--warn]="!isConsStatus">{{ row.userStatus }}</td>
									<td>
										@if (!row.isWarning && !row.isShipToConflict && isConsStatus && row.shipTo === headerShipTo()) {
											<button type="button" class="review-orders__icon-btn" aria-label="No warning">
												<span class="review-orders__icon review-orders__icon--warning-ok" aria-hidden="true"></span>
											</button>
										}@else{
											@if((row.userStatus && row.serialStatus) || row.isWarning || (row.shipTo !== headerShipTo() && row.shipTo)) {
											<button type="button" class="review-orders__icon-btn" aria-label="Warning present">
												<span class="review-orders__icon review-orders__icon--warning" aria-hidden="true"></span>
											</button>
											}@else{
											<p class="review-orders__field-error"></p>
											}
										}
									</td>
									<td>
										@if(!isReadOnly()){
											<button type="button" class="review-orders__icon-btn" (click)="deleteRow(row.id)" [disabled]="isDeleteLoading()" aria-label="Delete row">
												<span class="review-orders__icon review-orders__icon--delete" aria-hidden="true"></span>
											</button>
										}
									</td>
								</tr>
							}
						</tbody>
					</table>
				</div>
			</div>
		</section>

		<div class="review-orders__actions" aria-label="Review order actions">
			<button type="button" class="review-orders__outline-btn" (click)="goBack()">Back</button>
			@if(!isReadOnly()) {
			<div class="review-orders__actions-right">
				<button type="button" class="review-orders__outline-btn" (click)="addItem()">Add Item +</button>
				<button type="button" class="review-orders__outline-btn" (click)="reject()" [disabled]="isRejectConfigLoading()">Reject</button>
				<button type="button" class="review-orders__outline-btn" (click)="refresh()" [disabled]="!hasSerialNumber()">Refresh</button>
				@if (showUsFields()) {
					<button type="button" class="review-orders__outline-btn" [class.hasShipToMismatch]="!hasShipToMismatch()" (click)="sendToSN()" [disabled]="isSendToSNLoading() || !hasShipToMismatch()">Send SN to Consignment Review</button>
				}
				<button type="button" class="review-orders__primary-btn" (click)="placeOrder()" [disabled]="isPlaceOrderSaving() || pagedRowViews().length === 0">Place Order</button>
			</div>
			}
		</div>

		@if (showAccountModal()) {
			<app-jjsv-account-select
				[cartId]="modalCartId()"
				[accountType]="modalAccountType()"
				[initialData]="modalAccountData()"
				[contextAccountUid]="pendingShipTo()?.uid ?? ''"
				[contextAccountName]="pendingShipTo()?.displayName ?? ''"
				(accountSelected)="onAccountSelected($event)"
				(closed)="onAccountModalClose()"
			></app-jjsv-account-select>
		}

		@if (showConsignmentModal()) {
			<div class="review-orders__consignment-overlay" role="presentation">
				<section class="review-orders__consignment-modal" role="dialog" aria-modal="true" aria-labelledby="consignment-title">
					<button type="button" class="review-orders__consignment-close" (click)="closeConsignmentModal()" aria-label="Close consignment review modal">
						<span aria-hidden="true">X</span>
					</button>

					<h2 id="consignment-title" class="review-orders__consignment-title">Send SN to Consignment Review</h2>
					<p class="review-orders__consignment-subtitle">
						This document status will be changed to Awaiting consignment review and a notification will be sent to the selected recipients to review the associated accounts.
					</p>

					<div class="review-orders__consignment-table-wrap" role="region" aria-label="Consignment review serial numbers" tabindex="0">
						<table class="review-orders__consignment-table">
							<thead>
								<tr>
									<th>Material</th>
									<th>Serial Number</th>
									<th>Ship-To</th>
									<th>Transfer To</th>
								</tr>
							</thead>
							<tbody>
								@for (row of consignmentRows(); track $index) {
									<tr>
										<td>{{ row.material }}</td>
										<td>{{ row.serialNumber }}</td>
										<td>{{ row.shipto }}</td>
										<td>{{ row.transferToShipTo }}</td>
									</tr>
								}
							</tbody>
						</table>
					</div>

					<label class="review-orders__consignment-field">
						<span>Please provide a comment before proceeding <em>*</em></span>
						<input
							type="text"
							[value]="consignmentComment()"
							(input)="updateConsignmentComment($event)"
							[class.review-orders__field-invalid]="isConsignmentCommentInvalid()"
						/>
						@if (isConsignmentCommentInvalid()) {
							<small class="review-orders__field-error">Please provide a comment.</small>
						}
					</label>

					<label class="review-orders__consignment-field">
						<span>Provide additional emails to receive the notification <small>(Optional)</small></span>
						<input
							type="text"
							[value]="consignmentEmails()"
							(input)="updateConsignmentEmails($event)"
							[class.review-orders__field-invalid]="isConsignmentEmailsInvalid()"
						/>
						@if (isConsignmentEmailsInvalid()) {
							<small class="review-orders__field-error">Please enter valid email addresses.</small>
						}
					</label>

					<p class="review-orders__consignment-note">
						Once confirmed, a notification email will be sent to the selected recipient or team and to the additional emails provided, to review the accounts associated with the serial numbers listed above.
					</p>

					<div class="review-orders__consignment-actions">
						<button type="button" class="review-orders__outline-btn" (click)="closeConsignmentModal()">Cancel</button>
						<button type="button" class="review-orders__primary-btn" (click)="saveConsignmentReview()" [disabled]="isConsignmentSaving()">Save</button>
					</div>
				</section>
			</div>
		}

		@if (showRejectModal()) {
			<div class="review-orders__consignment-overlay" role="presentation">
				<section class="review-orders__consignment-modal review-orders__reject-modal" role="dialog" aria-modal="true" aria-labelledby="reject-title">
					<button type="button" class="review-orders__consignment-close" (click)="closeRejectModal()" aria-label="Close reject document modal">
						<span aria-hidden="true">X</span>
					</button>

					<h2 id="reject-title" class="review-orders__consignment-title">Reject Document</h2>
					<p class="review-orders__consignment-subtitle">
						Rejecting this document will put it in rejection status. This action cannot be undone.
					</p>

					<label class="review-orders__consignment-field">
						<span>Reason for rejection <em>*</em></span>
						<select
							[value]="rejectReason()"
							(change)="updateRejectReason($event)"
							[class.review-orders__field-invalid]="rejectSubmitted() && !rejectReason()"
						>
							<option value="" disabled>Select a reason</option>
							@for (reason of rejectReasonOptions(); track reason) {
								<option [value]="reason">{{ reason }}</option>
							}
						</select>
						@if (rejectSubmitted() && !rejectReason()) {
							<small class="review-orders__field-error">Please select a reason for rejection.</small>
						}
					</label>

					<label class="review-orders__consignment-field">
						<span>Additional details <small>(Optional)</small></span>
						<input type="text" placeholder="Add additional details (optional)" [value]="rejectAdditionalDetails()" (input)="updateRejectAdditionalDetails($event)" />
					</label>

					<label class="review-orders__consignment-field">
						<span>Notify @if (isRejectNotifyRequired()) { <em>*</em> }</span>
						<select
							[value]="rejectNotifyRecipient()"
							(change)="updateRejectNotifyRecipient($event)"
							[class.review-orders__field-invalid]="rejectSubmitted() && isRejectNotifyRequired() && !rejectNotifyRecipient()"
						>
							<option value="" disabled>Select recipient</option>
							@for (recipient of rejectNotifyOptions(); track recipient) {
								<option [value]="recipient">{{ recipient }}</option>
							}
						</select>
						@if (rejectSubmitted() && isRejectNotifyRequired() && !rejectNotifyRecipient()) {
							<small class="review-orders__field-error">Please select a recipient to notify.</small>
						}
					</label>

					<label class="review-orders__consignment-field">
						<span>Additional email recipients <small>(Optional)</small></span>
						<input type="text" placeholder="Additional email recipients (optional)" [value]="rejectAdditionalEmails()" (input)="updateRejectAdditionalEmails($event)" [class.review-orders__field-invalid]="isRejectEmailsInvalid()" />
						@if (isRejectEmailsInvalid()) {
							<small class="review-orders__field-error">Please enter valid email addresses.</small>
						}
						<small>Add one or more email addresses, separated by commas.</small>
					</label>

					<p class="review-orders__consignment-note">
						Once submitted, this document will be rejected and notification emails will be sent to the selected recipients.
					</p>

					<div class="review-orders__consignment-actions">
						<button type="button" class="review-orders__outline-btn" (click)="closeRejectModal()">Cancel</button>
						<button type="button" class="review-orders__primary-btn" (click)="saveRejectDocument()" [disabled]="isRejectSaving()">Save</button>
					</div>
				</section>
			</div>
		}
	</section>
</main>
